import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/react-hooks';

import { updateCallState } from '../../../../actions';
import useActions from '../../../../utils/useActions';
import { updateCallGQL } from '../../../../apollo';
import { Provider } from './Context';
import CallLogHistory from './CallLogHistory';
import ControlPanel from './ControlPanel';
import useTwilioDevice from './useTwilioDevice';

/**
 * The testing HP number is US number (cannot be changed because trial account)
 * HP: +19038475755
 * Please call using the console to test.
 * Change this to true if you want to test the incoming and outgoing calls.
 */
const isTesting = false;

// pulls state out of react lifecycle
const storage = {};

const blacklist = [
  '+919717392321',
  '+6583222468',
  '464',
  '+13474741886',
  '+919075091800',
  '+19294984890',
];

const Twilio = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [logs, setLogs] = useState([]);
  const [buttons, setButtons] = useState({});
  const state = useSelector(({ customers, leads, calls, user }) => ({
    customers,
    leads,
    user,
    calls,
  }));
  const [updateLog] = useActions([updateCallState]);
  const [updateOneCallLog] = useMutation(updateCallGQL, {
    onCompleted: ({ updateCall: result }) => {
      if (result && Object.keys(result).length > 0) {
        updateLog(result);
        toast.success('Successfully updated Call Log database.', {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.warn('Failed to update Call Log database', {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    },
    onError: (e) => {
      console.log(e);
      toast.warn('Failed to update Call Log database', {
        position: toast.POSITION.TOP_RIGHT,
      });
    },
  });

  storage.state = state;
  storage.buttons = buttons;

  /**
   * Updates call log panel with a new message.
   * @param {string} entry - Log message
   * @param {string} icon - Log icon
   */
  const addToCallLogs = (entry, icon, type = 'call') => {
    const getTime = () => {
      const time = new Date().toLocaleString('en-US', {
        hour: 'numeric',
        hour12: true,
        minute: 'numeric',
        second: 'numeric',
        timeZone: 'Asia/Hong_Kong',
      });
      const day = new Date().toLocaleDateString('en-GB', {
        month: 'short',
        day: 'numeric',
        timeZone: 'Asia/Hong_Kong',
      });

      return `${day}, ${time}`;
    };

    setLogs((prev) => {
      const newPrev = [
        {
          entry,
          icon,
          type,
          time: getTime(),
        },
        ...prev,
      ];

      return newPrev
        .splice(0, 100)
        .sort((currLog, prevLog) => currLog.time - prevLog.time);
    });
  };

  /**
   * Updates DB if in prod. If in test mode, show console.log.
   */
  const updateDB = (body) => {
    if (isTesting === true) {
      console.log('Testing mode: DB (assumed) to be updated!');
      return;
    }

    updateOneCallLog({ variables: { ...body } });
  };

  /**
   * Plays the user's selected ringtone (looped, best-effort). The SDK's own
   * incoming sound is muted inside the hook, so this is the only ring the user
   * hears. Stored on storage so accept/reject/disconnect/cancel can stop it.
   */
  const startRingtone = () => {
    if (storage.state.user.ringTone === 'default') {
      return;
    }

    const audio = new Audio(storage.state.user.ringTone);

    audio.loop = true;
    audio.play().catch(() => {});
    storage.ringtoneAudio = audio;
  };

  const stopRingtone = () => {
    if (storage.ringtoneAudio) {
      storage.ringtoneAudio.pause();
      storage.ringtoneAudio = null;
    }
  };

  /**
   * Handles incoming calls (2.x Call). Ported from the 1.x
   * TwilioDevice.on('incoming') handler.
   */
  const handleIncoming = (call) => {
    let ignored = false;

    /**
     * Reject blacklisted numbers.
     */
    if (blacklist.indexOf(call.parameters.From) >= 0) {
      call.reject();
      return;
    }

    startRingtone();

    const matchesInCustomerDatabase = storage.state.customers.filter((ele) => {
      // Some customers may not have their phone logged.
      if (ele.phone) {
        if (call.parameters.From.indexOf(ele.phone.trim()) > -1) {
          return true;
        }

        if (ele.phone.trim() === call.parameters.From) {
          return true;
        }
      }

      return false;
    })[0];
    const matchesInLeadsDatabase = storage.state.leads.filter((ele) => {
      // Some leads may not have their phone logged.
      if (ele.phone) {
        if (call.parameters.From.indexOf(ele.phone.trim()) > -1) {
          return true;
        }

        if (ele.phone.trim() === call.parameters.From) {
          return true;
        }
      }

      return false;
    })[0];
    const matchesInCallLogDatabase = storage.state.calls.filter(
      (ele) =>
        call.parameters.From === ele.from.trim() &&
        ele.status === 'missed' &&
        ele.handled !== true &&
        ele.sid !== call.parameters.CallSid
    );

    const ignoreCallCallback = () => {
      call.reject();
      if (call.parameters.From) {
        state.user.ws.sendAction(`ignored a call from ${call.parameters.From}.`);
      }

      stopRingtone();

      /**
       * Update call log if user ignored a call.
       */
      addToCallLogs(`Ignored ${call.parameters.From}`, 'call');

      ignored = true;
    };
    const acceptCallCallback = () => {
      call.accept();
      if (call.parameters.From) {
        state.user.ws.sendAction(
          `accepted a call from ${call.parameters.From}.`
        );
      }

      setTimeout(() => {
        /**
         * Updates the currently accepted call first.
         */
        updateDB({
          sid: call.parameters.CallSid,
          staff: storage.state.user.name,
          campus: 'BT',
          call: 'a',
          status: 'answered',
          from: call.parameters.From,
          time: new Date() - 3000,
          handled: true,
          handledBy: storage.state.user.name,
          handledTime: Date.now() - 3000,
        });

        /**
         * Updates the other missed call later.
         */
        matchesInCallLogDatabase.forEach((ele) => {
          const updatedEntry = {
            ...ele,
            handled: true,
            handledBy: storage.state.user.name,
            handledTime: Date.now() - 3000,
          };

          updateDB(updatedEntry);
        });
      }, 3000);
    };
    const hangUpCallCallback = () => {
      call.disconnect();
      if (call.parameters.From) {
        state.user.ws.sendAction(`hung up the call from ${call.parameters.From}.`);
      }
    };

    /**
     * Try to match customer db first, followed by leads db and lastly, show as
     * a new call if matches nothing. Update call log respectively.
     */
    if (matchesInCustomerDatabase) {
      addToCallLogs(
        `${matchesInCustomerDatabase.parent_first_name} ${
          matchesInCustomerDatabase.parent_last_name || ''
        } found in customer database`,
        'contact found'
      );
    } else if (matchesInLeadsDatabase) {
      addToCallLogs(
        `${matchesInLeadsDatabase.first_name} ${
          matchesInLeadsDatabase.last_name || ''
        } found in leads database`,
        'contact found'
      );
    } else {
      addToCallLogs(
        'No existing contact in database for this incoming call',
        'contact not found'
      );
    }

    /**
     * Add incoming call details to call log.
     */
    addToCallLogs(`Incoming ${call.parameters.From}`, 'bell');

    /**
     * Update call log if user accepts or hung up incoming call.
     */
    call.on('accept', () => {
      stopRingtone();
      addToCallLogs(`Received ${call.parameters.From}.`, 'call');
    });

    /**
     * Update call log if user ended the call.
     */
    call.on('disconnect', () => {
      stopRingtone();
      addToCallLogs(`Ended ${call.parameters.From}`, 'end call');
    });

    /**
     * Handles calls that were cancelled by the remote before we answered.
     * In 2.x, a remote cancel emits 'cancel' (not 'disconnect'), so the
     * missed-call reconciliation lives here. Ported from the 1.x
     * TwilioDevice.on('cancel') handler.
     */
    call.on('cancel', () => {
      stopRingtone();

      /**
       * Removes event from nodes.
       */
      storage.buttons.accept.ref.current.removeEventListener(
        'click',
        storage.buttons.accept.callback
      );
      storage.buttons.ignore.ref.current.removeEventListener(
        'click',
        storage.buttons.ignore.callback
      );
      storage.buttons.hangup.ref.current.removeEventListener(
        'click',
        storage.buttons.hangup.callback
      );

      /**
       * Do nothing if it is a blacklist call that we rejected.
       */
      if (blacklist.indexOf(call.parameters.From) >= 0) {
        return;
      }

      setTimeout(() => {
        /**
         * Do not update DB if we ignored the call.
         */
        if (ignored === true) {
          ignored = false;
          return;
        }

        const body = {
          sid: call.parameters.CallSid,
          staff: 'NA',
          campus: 'NA',
          call: 'a',
          status: 'missed',
          from: call.parameters.From,
          time: new Date() - 500,
        };

        /**
         * Update call log and DB if we have missed a call.
         */
        addToCallLogs(`Missed ${call.parameters.From}`, 'warning');

        updateDB(body);
      }, 500);
    });

    /**
     * Saved into state for removeEventListener later.
     */
    setButtons((prev) => {
      const newPrev = { ...prev };

      newPrev.accept.callback = acceptCallCallback;
      newPrev.ignore.callback = ignoreCallCallback;
      newPrev.hangup.callback = hangUpCallCallback;

      return newPrev;
    });

    /**
     * Update DB if user accepted a call.
     * If call ignored, we need to update the state so we can act on it appropriately.
     * Ignored and hangup calls will not enter DB.
     */
    storage.buttons.accept.ref.current.addEventListener(
      'click',
      acceptCallCallback,
      {
        once: true,
      }
    );
    storage.buttons.ignore.ref.current.addEventListener(
      'click',
      ignoreCallCallback,
      {
        once: true,
      }
    );
    storage.buttons.hangup.ref.current.addEventListener(
      'click',
      hangUpCallCallback,
      {
        once: true,
      }
    );
  };

  const { device, status, error } = useTwilioDevice(handleIncoming);

  /**
   * Handles making a Twilio call when user clicks on Make Call button.
   */
  const makeCall = async () => {
    if (typeof window === 'undefined' || !phoneNumber || !device) {
      return;
    }

    try {
      /**
       * Attempts to call the user using Twilio.
       */
      const call = await device.connect({ params: { To: phoneNumber } });

      state.user.ws.sendAction(`is calling ${phoneNumber}.`);

      /**
       * Adds to call log that we are calling the number.
       */
      addToCallLogs(`Calling ${phoneNumber}`, 'call');

      const matchingMissedCallEntriesCalled = storage.state.calls.filter(
        (ele) =>
          phoneNumber === ele.from &&
          ele.status === 'missed' &&
          ele.handled !== true
      );

      /**
       * Updates missed call list if user is making a call back to the number we missed
       * earlier. Set it as handled by current user.
       */
      matchingMissedCallEntriesCalled.forEach((ele) => {
        const updatedEntry = {
          ...ele,
          handled: true,
          handledBy: storage.state.user.name,
          handledTime: Date.now(),
        };

        updateDB(updatedEntry);
      });

      /**
       * Disconnects the phone call if user clicks on Hang Up button.
       */
      const hangUpCallCallback = () => {
        call.disconnect();
        const to = call.customParameters.get('To');

        if (to) {
          state.user.ws.sendAction(`hung up the call to ${to}.`);
        }
      };

      /**
       * Once the outgoing call connects, reconcile any missed-call entries for
       * the number we just dialled (the receiver's earlier missed call to us).
       */
      call.on('accept', () => {
        const to = call.customParameters.get('To');

        const matchesInCallLogDatabase = storage.state.calls.filter(
          (ele) =>
            to === ele.from.trim() &&
            ele.status === 'missed' &&
            ele.handled !== true &&
            ele.sid !== call.parameters.CallSid
        );
        const body = {
          sid: call.parameters.CallSid,
          staff: storage.state.user.name,
          campus: 'BT',
          call: 'a',
          outbound: true,
          status: 'called',
          from: to,
          time: new Date() - 3000,
          handled: true,
          handledBy: storage.state.user.name,
          handledTime: Date.now() - 3000,
        };

        updateDB(body);

        /**
         * Updates the other missed call later.
         */
        matchesInCallLogDatabase.forEach((ele) => {
          const updatedEntry = {
            ...ele,
            handled: true,
            handledBy: storage.state.user.name,
            handledTime: Date.now() - 3000,
          };

          updateDB(updatedEntry);
        });
      });

      /**
       * Update the call logs and clear the number we called once the call has
       * disconnected.
       */
      call.on('disconnect', () => {
        const to = call.customParameters.get('To');

        addToCallLogs(`Ended ${to}`, 'end call');

        setPhoneNumber('');
        storage.buttons.hangup.ref.current.removeEventListener(
          'click',
          hangUpCallCallback
        );
      });

      storage.buttons.hangup.ref.current.addEventListener(
        'click',
        hangUpCallCallback,
        {
          once: true,
        }
      );
    } catch (e) {
      addToCallLogs('Error: could not place the call. Please try again.', 'warning');
      toast.warn('Could not place the call. Please try again.', { position: toast.POSITION.TOP_RIGHT });
      console.log(e);
    }
  };

  /**
   * Surface hook status: log when Twilio becomes ready to accept calls.
   */
  useEffect(() => {
    if (status === 'registered') {
      addToCallLogs(
        `Twilio is ready to accept calls${isTesting === true ? ' (TEST)' : ''}`,
        'thumbs up'
      );
    }
  }, [status]);

  /**
   * The Voice SDK 2.x can only play its own built-in incoming ring (the old
   * `sounds.incoming` option is gone). So for the 'Twilio default' ringtone we
   * let the SDK ring; for a custom ringtone we mute the SDK and play our own
   * Audio in startRingtone(). Kept in sync with the user's ringtone choice.
   */
  useEffect(() => {
    if (device && status === 'registered') {
      device.audio.incoming(state.user.ringTone === 'default');
    }
  }, [device, status, state.user.ringTone]);

  /**
   * Surface hook errors: log them and show the mic-permission toast when the
   * browser blocked microphone access.
   */
  useEffect(() => {
    if (!error) {
      return;
    }

    addToCallLogs(
      `Error: ${
        error.message || error
      }. Please reload this page! If this persists, please inform the tech team.`,
      'warning'
    );

    if (
      error.name === 'NotAllowedError' ||
      error.code === 31401 ||
      error.code === 31402
    ) {
      toast.warn(
        'Microphone access is blocked. Please allow microphone permission and reload the page to make and receive calls.',
        {
          position: toast.POSITION.TOP_RIGHT,
        }
      );
    }
  }, [error]);

  /**
   * Broadcasts staff actions to the call log.
   */
  useEffect(() => {
    const memberCB = () => {};
    const actionCB = (e) => {
      addToCallLogs(`${e.detail.name} ${e.detail.action}`, 'info', 'action');
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('isOnline', memberCB);
      window.addEventListener('sendAction', actionCB);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('isOnline', memberCB);
        window.removeEventListener('sendAction', actionCB);
      }
    };
  }, []);

  return (
    <Provider
      value={{
        logs,
        setLogs,
        phoneNumber,
        setPhoneNumber,
        makeCall,
        setButtons,
      }}
    >
      <CallLogHistory />
      <ControlPanel />
    </Provider>
  );
};

export default Twilio;
